import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, where } from 'firebase/firestore';
import { ClipboardList, Minus, Plus, Search, ShoppingCart, Utensils } from 'lucide-react';
import { db } from '../../services/firebase';
import LoadingState from '../shared/LoadingState';
import { normalizePhoneKey, syncOrderTracking } from '../../utils/orderTracking';
import './PublicOrder.css';

const PublicOrder = () => {
  const { companySlug, establishmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [establishment, setEstablishment] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [cart, setCart] = useState([]);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [trackingPhone, setTrackingPhone] = useState('');
  const [trackingOrders, setTrackingOrders] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    table: '',
    notes: ''
  });

  useEffect(() => {
    loadPublicOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, establishmentId]);

  const loadPublicOrder = async () => {
    setLoading(true);
    setMessage('');
    try {
      const companiesRef = collection(db, 'companies');
      const slugSnap = await getDocs(query(companiesRef, where('slug', '==', companySlug)));
      let companyDoc = slugSnap.docs[0];

      if (!companyDoc) {
        const fallback = await getDoc(doc(db, 'companies', companySlug));
        companyDoc = fallback.exists() ? fallback : null;
      }

      if (!companyDoc) {
        setMessage('Empresa não encontrada.');
        return;
      }

      const companyData = { id: companyDoc.id, ...companyDoc.data() };
      const establishmentSnap = await getDoc(doc(db, `companies/${companyDoc.id}/establishments`, establishmentId));

      if (!establishmentSnap.exists() || establishmentSnap.data()?.active === false) {
        setMessage('Cardápio indisponível no momento.');
        return;
      }

      const menuSnap = await getDocs(collection(db, `companies/${companyDoc.id}/establishments/${establishmentId}/menuItems`));
      setCompany(companyData);
      setEstablishment({ id: establishmentSnap.id, ...establishmentSnap.data() });
      setMenuItems(menuSnap.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(item => item.active !== false)
      );
    } catch (error) {
      console.error('Erro ao carregar pedido público:', error);
      setMessage('Não foi possível carregar o cardápio.');
    } finally {
      setLoading(false);
    }
  };

  const groupedMenu = useMemo(() => {
    return menuItems.reduce((groups, item) => {
      const category = item.category || 'Outros';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
      return groups;
    }, {});
  }, [menuItems]);

  const totals = useMemo(() => {
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    return { itemCount, total };
  }, [cart]);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

  const formatDateTime = (date) => {
    if (!date) return '';
    const parsed = date.toDate ? date.toDate() : new Date(date);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusLabel = (status) => ({
    new: 'Recebido',
    preparing: 'Em preparo',
    ready: 'Pronto',
    delivered: 'Finalizado',
    cancelled: 'Cancelado'
  }[status] || 'Recebido');

  const getPaymentLabel = (status) => ({
    paid: 'Pago',
    pending: 'Aguardando pagamento',
    unpaid: 'Aguardando pagamento'
  }[status] || 'Aguardando pagamento');

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(cartItem => cartItem.id === item.id);
      if (existing) {
        return prev.map(cartItem => cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem);
      }
      return [...prev, {
        id: item.id,
        name: item.name,
        category: item.category,
        price: Number(item.price) || 0,
        quantity: 1
      }];
    });
  };

  const changeQuantity = (itemId, delta) => {
    setCart(prev => prev
      .map(item => item.id === itemId ? { ...item, quantity: item.quantity + delta } : item)
      .filter(item => item.quantity > 0)
    );
  };

  const submitOrder = async () => {
    if (!company?.id || !establishment?.id || cart.length === 0) {
      setMessage('Escolha pelo menos um item.');
      return;
    }
    if (!customer.name.trim() || !customer.phone.trim()) {
      setMessage('Informe seu nome e WhatsApp.');
      return;
    }

    setSaving(true);
    setMessage('');
    try {
      const orderPayload = {
        companyId: company.id,
        establishmentId: establishment.id,
        establishmentName: establishment.name,
        customerName: customer.name.trim(),
        customerPhone: customer.phone.trim(),
        table: customer.table.trim(),
        notes: customer.notes.trim(),
        source: 'public_link',
        status: 'new',
        items: cart.map(item => ({
          menuItemId: item.id,
          name: item.name,
          category: item.category || '',
          price: item.price,
          quantity: item.quantity,
          total: item.price * item.quantity
        })),
        itemCount: totals.itemCount,
        totalAmount: totals.total,
        paymentStatus: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      const orderRef = await addDoc(collection(db, `companies/${company.id}/orders`), orderPayload);
      await syncOrderTracking(company.id, { id: orderRef.id, ...orderPayload }, {
        orderId: orderRef.id,
        createdAt: serverTimestamp()
      });

      const phoneForTracking = customer.phone.trim();
      setCart([]);
      setCustomer({ name: '', phone: '', table: '', notes: '' });
      setTrackingPhone(phoneForTracking);
      await loadTrackingOrders(phoneForTracking);
      setMessage('Pedido enviado. Acompanhe o status pelo WhatsApp informado.');
    } catch (error) {
      console.error('Erro ao enviar pedido:', error);
      setMessage('Erro ao enviar pedido. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const loadTrackingOrders = async (phone = trackingPhone) => {
    if (!company?.id) return;
    const phoneKey = normalizePhoneKey(phone);
    if (!phoneKey) {
      setMessage('Informe o WhatsApp para acompanhar seus pedidos.');
      return;
    }

    setTrackingLoading(true);
    setMessage('');
    try {
      const trackingSnap = await getDocs(collection(db, `companies/${company.id}/publicOrderStatus/${phoneKey}/orders`));
      const data = trackingSnap.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(order => order.establishmentId === establishmentId)
        .sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return bTime - aTime;
        });
      setTrackingOrders(data);
      if (data.length === 0) {
        setMessage('Nenhum pedido encontrado para este WhatsApp neste cardápio.');
      }
    } catch (error) {
      console.error('Erro ao acompanhar pedido:', error);
      setMessage('Não foi possível consultar seus pedidos agora.');
    } finally {
      setTrackingLoading(false);
    }
  };

  if (loading) {
    return <LoadingState variant="screen" title="Carregando cardápio" subtitle="Preparando seu pedido" />;
  }

  return (
    <div className="public-order">
      <header className="public-order-hero">
        <div>
          <span>{company?.name || 'SDC Turismo'}</span>
          <h1>{establishment?.name || 'Cardápio'}</h1>
          <p>{establishment?.description || 'Escolha os itens e envie seu pedido para a equipe.'}</p>
        </div>
      </header>

      {message && <div className="public-order-message">{message}</div>}

      <main className="public-order-layout">
        <section className="public-menu">
          {Object.entries(groupedMenu).map(([category, items]) => (
            <div key={category} className="public-menu-section">
              <h2>{category}</h2>
              <div className="public-menu-grid">
                {items.map(item => (
                  <article key={item.id} className="public-menu-item">
                    {item.imageUrl && (
                      <img className="public-menu-image" src={item.imageUrl} alt={item.name} />
                    )}
                    <div>
                      <h3>{item.name}</h3>
                      {item.description && <p>{item.description}</p>}
                    </div>
                    <strong>{formatCurrency(item.price)}</strong>
                    <button onClick={() => addToCart(item)}>
                      <Plus size={18} />
                      Adicionar
                    </button>
                  </article>
                ))}
              </div>
            </div>
          ))}

          {menuItems.length === 0 && (
            <div className="public-empty">
              <Utensils size={44} />
              <p>Cardápio sem itens disponíveis no momento.</p>
            </div>
          )}
        </section>

        <aside className="public-cart">
          <div className="public-cart-title">
            <ShoppingCart size={22} />
            <h2>Seu pedido</h2>
          </div>

          <div className="public-cart-items">
            {cart.map(item => (
              <div key={item.id} className="public-cart-item">
                <div>
                  <strong>{item.name}</strong>
                  <span>{formatCurrency(item.price)}</span>
                </div>
                <div className="public-qty">
                  <button onClick={() => changeQuantity(item.id, -1)}><Minus size={14} /></button>
                  <b>{item.quantity}</b>
                  <button onClick={() => changeQuantity(item.id, 1)}><Plus size={14} /></button>
                </div>
              </div>
            ))}
            {cart.length === 0 && <p className="public-muted">Adicione itens ao pedido.</p>}
          </div>

          <div className="public-customer-form">
            <input value={customer.name} onChange={(event) => setCustomer({ ...customer, name: event.target.value })} placeholder="Seu nome" />
            <input value={customer.phone} onChange={(event) => setCustomer({ ...customer, phone: event.target.value })} placeholder="WhatsApp" />
            <input value={customer.table} onChange={(event) => setCustomer({ ...customer, table: event.target.value })} placeholder="Mesa, quarto ou referência" />
            <textarea rows="3" value={customer.notes} onChange={(event) => setCustomer({ ...customer, notes: event.target.value })} placeholder="Observações do pedido" />
          </div>

          <div className="public-total">
            <span>Total</span>
            <strong>{formatCurrency(totals.total)}</strong>
          </div>
          <button className="public-submit" onClick={submitOrder} disabled={saving || cart.length === 0}>
            {saving ? 'Enviando...' : 'Enviar pedido'}
          </button>

          <div className="public-tracking">
            <div className="public-cart-title">
              <ClipboardList size={21} />
              <h2>Acompanhar</h2>
            </div>
            <div className="public-tracking-form">
              <input
                value={trackingPhone}
                onChange={(event) => setTrackingPhone(event.target.value)}
                placeholder="WhatsApp do pedido"
              />
              <button onClick={() => loadTrackingOrders()} disabled={trackingLoading}>
                <Search size={16} />
                {trackingLoading ? 'Buscando...' : 'Consultar'}
              </button>
            </div>
            <div className="public-tracking-list">
              {trackingOrders.map(order => (
                <div key={order.id} className={`public-tracking-item status-${order.status}`}>
                  <div>
                    <strong>{getStatusLabel(order.status)}</strong>
                    <span>{formatDateTime(order.createdAt)} · {order.table || order.customerName || 'Pedido'}</span>
                  </div>
                  <div>
                    <b>{formatCurrency(order.totalAmount)}</b>
                    <small>{getPaymentLabel(order.paymentStatus)}</small>
                  </div>
                </div>
              ))}
              {trackingOrders.length === 0 && <p className="public-muted">Informe o WhatsApp para ver seus pedidos.</p>}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default PublicOrder;
